/**
 * Serviço Discord - Envio de erros do frontend
 */

import { discordConfig } from './config';
import type { DiscordError, DiscordErrorContext } from './types';

interface FrontendErrorPayload {
  type: DiscordError['type'];
  error: {
    message?: string;
    stack?: string;
    name?: string;
    [key: string]: any;
  };
  context?: DiscordErrorContext;
  severity?: DiscordError['severity'];
  environment?: string;
}

/**
 * Obtém informações do usuário logado do localStorage
 * Evita dependência circular com o userStore
 */
function getUserInfo(): Partial<DiscordErrorContext> {
  try {
    const userData = localStorage.getItem('user');
    if (!userData) return {};
    
    const user = JSON.parse(userData);
    return {
      userId: user.userId || user.id,
      userName: user.name,
      userEmail: user.email,
      companyId: user.companyId,
    };
  } catch {
    return {};
  }
}

class DiscordService {
  private config = discordConfig;

  /**
   * Envia uma notificação de erro para o Discord
   */
  async notifyError(discordError: DiscordError): Promise<void> {
    if (!this.config.enabled) {
      console.debug('Discord notifications disabled');
      return;
    }

    try {
      // Enriquecer contexto com informações do usuário
      const userInfo = getUserInfo();
      discordError.context = {
        ...userInfo,
        ...discordError.context,
      };

      const payload = this.buildBackendPayload(discordError);

      await fetch(this.config.endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      console.log(`✅ Erro enviado ao Discord: ${discordError.type}`);
    } catch (error) {
      console.error('❌ Falha ao enviar erro ao Discord:', error);
    }
  }

  /**
   * Constrói o payload enviado ao backend
   */
  private buildBackendPayload(discordError: DiscordError): FrontendErrorPayload {
    return {
      type: discordError.type,
      error: this.serializeError(discordError.error),
      context: discordError.context,
      severity: discordError.severity,
      environment: discordError.environment || this.config.environment,
    };
  }

  private serializeError(error: any): FrontendErrorPayload['error'] {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }

    if (typeof error === 'string') {
      return { message: error };
    }

    return {
      ...(error || {}),
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
    };
  }

}

export const discordService = new DiscordService();

